import { Visibility, VisibilityOff } from '@mui/icons-material';
import { Box, Button, IconButton, InputAdornment, Typography } from '@mui/material';
import { Formik } from 'formik';
import { DateTime } from 'luxon';
import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useDispatch } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import * as Yup from 'yup';
import { authClient } from '../../api/client';
import { AuthJWTResponseResult } from '../../api/schema';
import { seTwoFactor, setUserAuth } from '../../redux/slices/users/usersSlice';
import { Alert } from '../../styled/components';
import useAuthRedirect from './hooks/useAuthRedirect';
import Styled from './signIn.styled';

export default function SignIn(): React.ReactElement {
  const dispatch = useDispatch();
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const defineRedirect = useAuthRedirect();

  const handleSubmit = async (values, { setErrors }) => {
    try {
      const response = await authClient.loginUser(
        {
          identity: values.email.toLowerCase(),
          identity_type: 'email',
          password: values.password,
        },
        { skipRedirect: true },
      );

      const authResp: AuthJWTResponseResult | undefined = response.data.result;
      if (authResp?.two_factor_required) {
        dispatch(setUserAuth(authResp));

        const {
          data: { result: twoFactor },
        } = await authClient.twofactorSend();

        if (twoFactor) {
          dispatch(seTwoFactor({ ...twoFactor, now: DateTime.now().toISO() }));
          navigate('/auth/2fa', { replace: true });
        }
      } else {
        defineRedirect(authResp);
      }

      // eslint-disable-next-line
      // @ts-ignore
    } catch (error: Error) {
      const message = error.response?.data?.messages || error.message || 'Something went wrong';
      setErrors({ submit: message });
      console.log(error); // eslint-disable-line
    }
  };

  return (
    <React.Fragment>
      <Styled.Brand />
      <Styled.Wrapper>
        <Helmet title="Sign In" />

        <Typography component="h1" variant="h4" align="center" gutterBottom>
          Welcome to the Tag System!
        </Typography>
        <Typography component="h2" variant="body1" align="center">
          Sign in to your account to continue
        </Typography>

        <Formik
          initialValues={{
            email: '',
            password: '',
            submit: false,
          }}
          validationSchema={Yup.object().shape({
            email: Yup.string().email('Must be a valid email').max(255).required('Email is required'),
            password: Yup.string().max(255).required('Password is required'),
          })}
          onSubmit={handleSubmit}
        >
          {({ errors, handleBlur, handleChange, handleSubmit, isSubmitting, touched, values }) => (
            <form noValidate onSubmit={handleSubmit}>
              {errors.submit && (
                <Alert mt={2} mb={4} severity="warning">
                  {Array.isArray(errors.submit) ? errors.submit.map((item) => <Box key="item">{item}</Box>) : errors.submit}
                </Alert>
              )}
              <Styled.TextField
                type="email"
                name="email"
                label="Email Address"
                value={values.email}
                error={Boolean(touched.email && errors.email)}
                fullWidth
                helperText={touched.email && errors.email}
                onBlur={handleBlur}
                onChange={handleChange}
                my={2}
                inputProps={{
                  style: { textTransform: 'lowercase' },
                }}
              />
              <Styled.TextField
                type={showPassword ? 'text' : 'password'}
                name="password"
                label="Password"
                value={values.password}
                error={Boolean(touched.password && errors.password)}
                fullWidth
                helperText={touched.password && errors.password}
                onBlur={handleBlur}
                onChange={handleChange}
                my={2}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPassword((value) => !value)} edge="end">
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <Button sx={{ mt: 4 }} type="submit" fullWidth variant="contained" color="primary" disabled={isSubmitting}>
                Sign in
              </Button>
              <Button component={Link} to="/auth/reset-password" fullWidth color="primary" sx={{ mt: 8 }}>
                Forgot password
              </Button>
            </form>
          )}
        </Formik>
      </Styled.Wrapper>
    </React.Fragment>
  );
}
